import map from 'lodash/map'
import React from 'react'

import { Loading } from '@Components/Modal/Loading'
import { ShippingModalUrgencyTimer } from '@Components/Shipping/ShippingPricing/UrgencyTimer'
import { ShippingInfoQuery, TextStyle, useShippingInfoQuery } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useCheapestShippingPrice, useFreeShippingThreshold } from '@Globals/Metadata/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './ShippingPricingV2.module.scss'

type ShippingInfoType = NonNullable<ShippingInfoQuery['shippingInfo']>
type ShippingInfoOption = NonNullable<ShippingInfoType['items']>[0]

export const ShippingPricingV2 = ({
  id,
  showFreeShippingDescription,
}: {
  id?: string
  showFreeShippingDescription: boolean
}) => {
  const { store } = React.useContext(MetadataContext)
  const { data, error, loading } = useShippingInfoQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const isLoading = (!store || loading) && !error
  const { t, tDanger } = useTranslation()
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()
  const isGlobalShippingFree = cheapestShippingPrice === null || cheapestShippingPrice?.value === 0
  const shippingOptions: ShippingInfoOption[] = data?.shippingInfo?.items || []

  if (isLoading) { return <div className={styles.loading}><Loading /></div> }

  return (
    <>
      <div className={styles.banner} id={id}>
        {isGlobalShippingFree && <div className={styles.shippingDescription}>{t('header:FREE_STANDARD_SHIPPING_ALL_ORDERS')}</div>}
        {!isGlobalShippingFree && freeShippingThreshold && showFreeShippingDescription && (
          <div
            {...tDanger(
              'header:DELIVERY_INFO_INTRO_TEXT_WITH_THRESHOLD',
              { arg1: freeShippingThreshold.format },
            )}
            className={styles.shippingDescription}
          />
        )}
        <ShippingModalUrgencyTimer className={styles.urgency} />
      </div>

      <div className={styles.optionsTable}>
        <dl className={styles.header}>
          <dt>{t('common:DELIVERY_LABEL')}</dt>
          <dd className={styles.deliveryTime}>{t('cms:COF_DELIVERY_TIME')}</dd>
          <dd className={styles.price}>{t('common:CART_PRODUCT_LIST_PRICE_LABEL', 'price')}</dd>
        </dl>

        {map(shippingOptions, ((option, i) => (
          <dl key={`${option.title}_${option.deliveryTime?.text}_${option.price?.value?.format}${i}`}>
            <dt>{option.title}</dt>

            <dd className={styles.logo}>
              <img
                alt={option.title || ''}
                loading="lazy"
                src={option.logo?.url}
              />
            </dd>

            {option.deliveryTime && (
              <dd className={styles.deliveryTime}>
                {option.deliveryTime.text}
              </dd>
            )}

            {option.priceBefore?.value?.format && (
              <dd className={styles.priceBefore}>
                {option.priceBefore?.value?.format}
              </dd>
            )}

            <dd className={styles.price}>
              {option.price?.text
                ? <strong>{option.price?.text}</strong>
                : option.price?.value?.format}
            </dd>

            {/* TODO: better condition than bold to check on */}
            {option.priceAboveThreshold?.style === TextStyle.Bold && freeShippingThreshold?.format && (
              <dd className={styles.threshold}>
                {t('cms:FREE_OVER', { amount: freeShippingThreshold.format })}
              </dd>
            )}
          </dl>
        )))}
      </div>
    </>
  )
}
